/* eslint-disable no-param-reassign */
import { WritableDraft } from 'immer';
import { core_v1_DataSource_FieldSpec } from '@antimatterhq/asl-api-ts-v1';
import { deepEqual } from '../../datasourceUtils';
import {
  DatasourceFieldSpecPresetValues,
  DatasourceUpgradeFieldSpecDiff,
  DatasourceUpgradeFieldSpecStatus,
  DatasourceUpgradeFieldStatus,
  DatasourceUpgradeStatus,
} from '../datasourceUpgrade.types';
import {
  acceptSilverField,
  acceptTemporarySilverField,
  rejectSilverField,
  rejectTemporarySilverField,
} from '../silver/datasourceUpgradeSilverHelper';
import {
  acceptGoldField,
  rejectGoldField,
} from '../gold/datasourceUpgradeGoldHelper';
import {
  acceptPreTransformField,
  acceptTemporaryPreTransformField,
  rejectPreTransformField,
  rejectTemporaryPreTransformField,
} from '../preTransform/datasourceUpgradePreTransformHelper';
import {
  Datasource,
  Datasource_FieldSpec,
  Datasource_FieldUtils,
} from '@/types/api';
import { exhaustiveMatchingGuard } from '@/utils/util';

export const rejectTemporaryField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  utils: WritableDraft<Datasource_FieldUtils>,
) => {
  if (!utils.temporaryFields) {
    utils.temporaryFields = [];
  }

  // regardless of whether we are rejecting an added/changed/removed field we filter it out
  utils.temporaryFields = utils.temporaryFields.filter(
    (field) => field?.name !== fieldDiff.fieldName,
  );

  // if we are rejecting an added temporary field then we are done
  if (!fieldDiff.currentPresetValue || !fieldDiff.currentValue) {
    return;
  }

  // else we are rejecting a changed/removed temporary field
  utils.temporaryFields.push(fieldDiff.currentValue);
};

export const rejectField = (
  setOmitFields: (fields: string[]) => void,
  setAddFields: (fields: core_v1_DataSource_FieldSpec[]) => void,
  omitFields: string[],
  addFields: core_v1_DataSource_FieldSpec[],
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
) => {
  if (fieldDiff.upgradePresetValue) {
    // add to omit
    setOmitFields([...(omitFields || []), fieldDiff.fieldName]);
  }

  // if this is a new field that we are rejecting then we are done
  if (!fieldDiff.currentPresetValue) {
    return;
  }

  // every other case copy the current preset field
  const currentPresetField = fieldDiff.currentPresetValue;
  const newAddFields = addFields.filter(
    (field) => field?.name !== fieldDiff.fieldName,
  );
  setAddFields([...newAddFields, { ...currentPresetField }]);
};

export const acceptTemporaryField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  utils: WritableDraft<Datasource_FieldUtils>,
) => {
  if (!utils.temporaryFields) {
    utils.temporaryFields = [];
  }

  // regardless of whether we are accepting an added/changed/removed field we filter it out
  utils.temporaryFields = utils.temporaryFields.filter(
    (field) => field?.name !== fieldDiff.fieldName,
  );

  if (fieldDiff.upgradePresetValue) {
    utils.temporaryFields.push(fieldDiff.upgradePresetValue);
  }
};

export const acceptField = (
  setOmitFields: (fields: string[]) => void,
  setAddFields: (fields: core_v1_DataSource_FieldSpec[]) => void,
  omitFields: string[],
  addFields: core_v1_DataSource_FieldSpec[],
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
) => {
  const { fieldName } = fieldDiff;

  setAddFields(addFields.filter((field) => field?.name !== fieldName));

  setOmitFields(omitFields.filter((field) => field !== fieldName));
};

export const rejectFieldSpecValue = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { stage, isTemporary } = fieldDiff;

  switch (stage) {
    case 'preTransform': {
      if (isTemporary) {
        rejectTemporaryPreTransformField(fieldDiff, datasource);
      } else {
        rejectPreTransformField(fieldDiff, datasource);
      }
      return;
    }
    case 'silver': {
      if (isTemporary) {
        rejectTemporarySilverField(fieldDiff, datasource);
      } else {
        rejectSilverField(fieldDiff, datasource);
      }
      return;
    }
    case 'gold': {
      if (isTemporary) {
        throw new Error('Cannot reject temporary field for gold');
      }
      rejectGoldField(fieldDiff, datasource);
      return;
    }
    default: {
      return exhaustiveMatchingGuard(stage);
    }
  }
};

export const acceptFieldSpecValue = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { stage, isTemporary } = fieldDiff;

  switch (stage) {
    case 'preTransform': {
      if (isTemporary) {
        acceptTemporaryPreTransformField(fieldDiff, datasource);
      } else {
        acceptPreTransformField(fieldDiff, datasource);
      }
      return;
    }
    case 'silver': {
      if (isTemporary) {
        acceptTemporarySilverField(fieldDiff, datasource);
      } else {
        acceptSilverField(fieldDiff, datasource);
      }
      return;
    }
    case 'gold': {
      if (isTemporary) {
        throw new Error('Cannot accept temporary field for gold');
      }
      acceptGoldField(fieldDiff, datasource);
      return;
    }
    default: {
      return exhaustiveMatchingGuard(stage);
    }
  }
};

export const equalFieldSpec = (
  a: Datasource_FieldSpec | undefined,
  b: Datasource_FieldSpec | undefined,
) => {
  return deepEqual(
    a ? { ...a, comment: undefined } : a,
    b ? { ...b, comment: undefined } : b,
  );
};

export const getFieldSpecStatus = ({
  customValue,
  upgradePresetValue,
  currentPresetValue,
  omitFields = [],
}: {
  customValue?: Datasource_FieldSpec | undefined;
  upgradePresetValue?: Datasource_FieldSpec | undefined;
  currentPresetValue?: Datasource_FieldSpec | undefined;
  omitFields?: string[];
}): DatasourceUpgradeFieldStatus => {
  if (equalFieldSpec(upgradePresetValue, currentPresetValue)) {
    return 'unchanged';
  }

  // for normal fields we only to check if the custom field exists, for temporary fields we need to check if the custom field exists and doesn't match the current preset value
  if (customValue && !equalFieldSpec(customValue, currentPresetValue)) {
    return 'conflicted';
  }

  if (omitFields.includes(upgradePresetValue?.name || '')) {
    return 'unchanged';
  }

  return 'unapplied';
};

export const setFieldSpecStatus = ({
  draft,
  stage,
  fieldName,
  status,
  tableName,
  isTemporary,
}: {
  draft: WritableDraft<DatasourceUpgradeStatus>;
  stage: 'preTransform' | 'silver' | 'gold';
  fieldName: string;
  status: DatasourceUpgradeFieldStatus;
  tableName?: string;
  isTemporary?: boolean;
}) => {
  if (stage === 'preTransform') {
    const field = isTemporary
      ? draft.spec.silver.preTransform.utils?.temporaryFields?.find(
          ({ name }) => name === fieldName,
        )
      : draft.spec.silver.preTransform.fields.find(
          ({ name }) => name === fieldName,
        );

    if (!field) {
      throw new Error(`Field ${fieldName} not found in ${stage}`);
    }

    field.status = status;
    return;
  }

  if (stage === 'silver') {
    const table = draft.spec.silver.transform.find(
      ({ name }) => name === tableName,
    );

    if (!table) {
      throw new Error(`Table ${tableName} not found in ${stage}`);
    }

    const field = isTemporary
      ? table.utils?.temporaryFields?.find(({ name }) => name === fieldName)
      : table.fields.find(({ name }) => name === fieldName);

    if (!field) {
      throw new Error(`Field ${fieldName} not found in ${stage}`);
    }

    field.status = status;
    return;
  }

  if (stage === 'gold') {
    const table = draft.spec.gold.find(({ name }) => name === tableName);

    if (!table) {
      throw new Error(`Table ${tableName} not found in ${stage}`);
    }

    const field = table.fields.find(({ name }) => name === fieldName);

    if (!field) {
      throw new Error(`Field ${fieldName} not found in ${stage}`);
    }

    field.status = status;
  }
};

export const getFieldSpecDiff = ({
  stage,
  status,
  customField,
  upgradeCustomField,
  upgradeOmitted,
  upgradePresetValue,
  currentPresetValue,
  tableName,
  isTemporary,
}: {
  stage: 'preTransform' | 'silver' | 'gold';
  status: DatasourceUpgradeFieldStatus;
  customField?: Datasource_FieldSpec;
  upgradeCustomField?: Datasource_FieldSpec;
  upgradeOmitted: boolean;
  tableName?: string;
  isTemporary?: boolean;
} & DatasourceFieldSpecPresetValues): DatasourceUpgradeFieldSpecDiff => {
  const fieldName = upgradePresetValue?.name || currentPresetValue?.name || '';
  const diff: DatasourceUpgradeFieldSpecDiff = {
    stage,
    tableName,
    fieldName,
    status,
    currentValue: customField,
    upgradeValue: upgradeCustomField,
    upgradeOmitted,
    upgradePresetValue,
    currentPresetValue,
    updateStatus: (params: {
      draft: WritableDraft<DatasourceUpgradeStatus>;
      status: DatasourceUpgradeFieldStatus;
    }) =>
      setFieldSpecStatus({
        ...params,
        stage,
        fieldName,
        tableName,
        isTemporary,
      }),
    acceptChanges: (params: {
      diff: DatasourceUpgradeFieldSpecDiff;
      datasource: WritableDraft<Datasource>;
    }) => acceptFieldSpecValue(params.diff, params.datasource),
    rejectChanges: (params: {
      diff: DatasourceUpgradeFieldSpecDiff;
      datasource: WritableDraft<Datasource>;
    }) => rejectFieldSpecValue(params.diff, params.datasource),
    type: 'fieldSpec',
    isTemporary: isTemporary || false,
  };

  return diff;
};

export const getFieldsSpecStatuses = ({
  customFields = [],
  upgradeVersionFields = [],
  currentVersionFields = [],
  omitFields = [],
}: {
  customFields?: Datasource_FieldSpec[];
  upgradeVersionFields?: Datasource_FieldSpec[];
  currentVersionFields?: Datasource_FieldSpec[];
  omitFields?: string[];
} = {}): DatasourceUpgradeFieldSpecStatus[] => {
  const upgradeVersionNames = upgradeVersionFields.map(
    (field) => field.name || '',
  );
  const currentVersionNames = currentVersionFields.map(
    (field) => field.name || '',
  );
  // Create a Set to ensure unique names from both version arrays
  const names = [
    ...new Set([...upgradeVersionNames, ...currentVersionNames]),
  ].filter((q) => q !== '');

  return names
    .map((name) => ({
      name,
      status: getFieldSpecStatus({
        customValue: customFields.find((q) => q.name === name),
        upgradePresetValue: upgradeVersionFields.find((q) => q.name === name),
        currentPresetValue: currentVersionFields.find((q) => q.name === name),
        omitFields,
      }),
    }))
    .filter((q) => q.status !== 'unchanged');
};

export const getFieldsSpecDiffs = ({
  stage,
  statuses,
  customFields = [],
  upgradeCustomFields = [],
  upgradeVersionFields = [],
  currentVersionFields = [],
  upgradeOmitFields,
  tableName,
  isTemporary,
}: {
  stage: 'preTransform' | 'silver' | 'gold';
  statuses: DatasourceUpgradeFieldSpecStatus[];
  customFields?: Datasource_FieldSpec[];
  upgradeCustomFields?: Datasource_FieldSpec[];
  upgradeVersionFields?: Datasource_FieldSpec[];
  currentVersionFields?: Datasource_FieldSpec[];
  upgradeOmitFields: string[];
  tableName?: string;
  isTemporary?: boolean;
}): DatasourceUpgradeFieldSpecDiff[] => {
  return statuses.map((status) => {
    const currentPresetValue = currentVersionFields.find(
      (q) => q.name === status.name,
    );
    const upgradePresetValue = upgradeVersionFields.find(
      (q) => q.name === status.name,
    );
    const customField = customFields.find((q) => q.name === status.name);
    const upgradeCustomField = upgradeCustomFields.find(
      (q) => q.name === status.name,
    );

    const diff = getFieldSpecDiff({
      stage,
      status: status.status,
      customField,
      upgradeCustomField,
      upgradePresetValue,
      currentPresetValue,
      tableName,
      isTemporary,
      upgradeOmitted: upgradeOmitFields?.includes(status.name) || false,
    });

    return diff;
  });
};

export const setFieldSpecText = (fieldDiff: DatasourceUpgradeFieldSpecDiff) => {
  const { fieldName, status, upgradePresetValue, currentPresetValue } =
    fieldDiff;

  if (status === 'unchanged') {
    return;
  }

  fieldDiff.title = fieldName;

  const type = fieldDiff.isTemporary ? 'temporary field' : 'field';

  if (!currentPresetValue) {
    fieldDiff.description = `"${fieldName}" ${type} added`;
    return;
  }

  if (!upgradePresetValue) {
    fieldDiff.description = `"${fieldName}" ${type} removed`;
    return;
  }

  if (currentPresetValue.alias && upgradePresetValue.alias) {
    fieldDiff.description = `"${fieldName}" ${type} alias changed`;
    return;
  }

  if (upgradePresetValue.alias) {
    fieldDiff.description = `"${fieldName}" ${type} changed to an alias`;
    return;
  }

  if (currentPresetValue.expr && upgradePresetValue.expr) {
    fieldDiff.description = `"${fieldName}" ${type} changed expression value`;
    return;
  }

  if (upgradePresetValue.expr) {
    fieldDiff.description = `"${fieldName}" ${type} changed to a SQL Expression`;
    return;
  }

  if (currentPresetValue.literal && upgradePresetValue.literal) {
    fieldDiff.description = `"${fieldName}" ${type} changed literal value to "${upgradePresetValue.literal}"`;
    return;
  }

  if (upgradePresetValue.literal) {
    fieldDiff.description = `"${fieldName}" ${type} changed to Literal`;
    return;
  }

  if (currentPresetValue.from && upgradePresetValue.from) {
    fieldDiff.description = `"${fieldName}" ${type} changed source field`;
    return;
  }

  if (upgradePresetValue.from) {
    fieldDiff.description = `"${fieldName}" ${type} changed to a source field`;
    return;
  }
};
